import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import SmoothScroll from "smooth-scroll";

import Layout from "./components/layout";
import { ScrollToTop } from "./components/ScrollToTop";
import { BannerSlider } from "./components/bannerSlider";
import { Arrived } from "./components/arrived";
import { Affects } from "./components/affects";
import { OneInTen } from "./components/oneinten";
import { Articles } from "./components/articles";
import { About } from "./components/about";
import { Newsletter } from "./components/newsletter";
import { Collaborators } from "./components/collaborators";
import { Services } from "./components/services";
import { Instagram } from "./components/instagram";
import { Contact } from "./components/contact";
import instagramConfig from "./config/instagram";
import { MoreInfoLayout } from "./components/more_info/Layout";
import Reviews from "./components/reviews";
import { ConstructionModal } from "./components/ConstructionModal";

import { loadLocaleData } from "./utils/i18n";
import { initializeAnalytics } from "./config/analytics";

import "./App.css";

// Import the default data
import defaultData from "./data/data.json";

// Toggle this to show/hide construction modal
const SHOW_CONSTRUCTION_MODAL = false;

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
});

interface LandingPageData {
  Services?: any;
  Contact?: any;
}

const App: React.FC = () => {
  const [locale] = useState("es");
  const [landingPageData, setLandingPageData] = useState<LandingPageData>({});

  useEffect(() => {
    async function fetchLocaleData() {
      const localeData = await loadLocaleData(locale);
      const mergedData = { ...defaultData, ...localeData };
      setLandingPageData(mergedData);
    }
    fetchLocaleData();
    
    // Initialize analytics
    initializeAnalytics();
  }, [locale]);

  // Handle Spanish URL hash mapping for home page
  useEffect(() => {
    const hashToIdMap: { [key: string]: string } = {
      "#por_que": "why",
      "#considera": "consider",
      "#acerca": "about",
      "#guia": "guide",
    };

    const handleHashChange = () => {
      const hash = window.location.hash;
      if (window.location.pathname === "/" && hash && hashToIdMap[hash]) {
        setTimeout(() => {
          document.getElementById(hashToIdMap[hash])?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    };

    // Handle initial load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Show construction modal if enabled
  if (SHOW_CONSTRUCTION_MODAL) {
    return <ConstructionModal />;
  }

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route
            index
            element={
              <div>
                <BannerSlider />
                <Arrived />
                <Affects />
                <OneInTen />
                <Articles />
                <About />
                <Newsletter />
                <Services data={landingPageData.Services} />
                <Instagram 
                  limit={instagramConfig.postsLimit} 
                  username={instagramConfig.username} 
                />
                <Collaborators />
                <Contact data={landingPageData.Contact} />
              </div>
            }
          />
          <Route path="/aprende" element={<MoreInfoLayout />} />
          <Route path="/reviews" element={<Reviews />} />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;